import React from 'react';
import { Box, Typography } from '@mui/material';

export type CardRendererPreviewType = 'legacy' | 'fixedCardV2' | 'b2bOptimized';

const MiniBlock = ({
  sx = {},
}: {
  sx?: Record<string, string | number>;
}) => (
  <Box
    sx={{
      bgcolor: '#e5e7eb',
      borderRadius: 0.5,
      ...sx,
    }}
  />
);

const LegacyLayoutPreview = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, p: 1 }}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Box
        key={i}
        sx={{
          border: '1px solid #d1d5db',
          borderRadius: 1,
          bgcolor: '#fff',
          overflow: 'hidden',
        }}
      >
        <MiniBlock sx={{ height: 28, bgcolor: '#f3f4f6' }} />
        <Box sx={{ p: 0.5 }}>
          <MiniBlock sx={{ height: 4, mb: 0.5, width: '90%' }} />
          <MiniBlock sx={{ height: 4, mb: 0.5, width: '60%' }} />
          <MiniBlock sx={{ height: 6, width: '45%', bgcolor: '#93c5fd' }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const FixedCardV2LayoutPreview = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, p: 1 }}>
    {Array.from({ length: 3 }).map((_, i) => (
      <Box
        key={i}
        sx={{
          border: '1px solid #d1d5db',
          borderRadius: 1.5,
          bgcolor: '#fff',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        <MiniBlock sx={{ height: 36, bgcolor: '#f9fafb' }} />
        <Box sx={{ p: 0.75 }}>
          <MiniBlock sx={{ height: 5, mb: 0.5, width: '95%' }} />
          <MiniBlock sx={{ height: 4, mb: 0.75, width: '55%' }} />
          <Box
            sx={{
              border: '1.5px solid #f5c04f',
              borderRadius: 0.75,
              px: 0.5,
              py: 0.25,
              display: 'inline-block',
            }}
          >
            <MiniBlock sx={{ height: 5, width: 28, bgcolor: '#1976d2' }} />
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

const B2BOptimizedLayoutPreview = () => (
  <Box sx={{ display: 'flex', gap: 0.75, p: 1, minHeight: 100 }}>
    <Box
      sx={{
        width: '28%',
        border: '1px solid #d1d5db',
        borderRadius: 1,
        bgcolor: '#f9fafb',
        p: 0.75,
        flexShrink: 0,
      }}
    >
      <MiniBlock sx={{ height: 5, mb: 0.75, width: '80%' }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <MiniBlock sx={{ width: 6, height: 6, borderRadius: 0.25 }} />
          <MiniBlock sx={{ height: 4, flex: 1 }} />
        </Box>
      ))}
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
        <MiniBlock sx={{ height: 8, flex: 1, borderRadius: 1 }} />
        {['Tablo', 'Liste', 'Kart'].map((_, i) => (
          <MiniBlock key={i} sx={{ height: 8, width: 18, borderRadius: 1, bgcolor: i === 1 ? '#bfdbfe' : '#e5e7eb' }} />
        ))}
      </Box>
      {Array.from({ length: 4 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            borderBottom: '1px solid #f3f4f6',
            pb: 0.5,
          }}
        >
          <MiniBlock sx={{ width: 14, height: 14, borderRadius: 0.5, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <MiniBlock sx={{ height: 4, mb: 0.25, width: '70%' }} />
            <MiniBlock sx={{ height: 3, width: '40%' }} />
          </Box>
          <MiniBlock sx={{ height: 5, width: 22 }} />
          <MiniBlock sx={{ height: 8, width: 8, borderRadius: 0.5, bgcolor: '#dbeafe' }} />
        </Box>
      ))}
    </Box>
  </Box>
);

const previewMap: Record<CardRendererPreviewType, React.FC> = {
  legacy: LegacyLayoutPreview,
  fixedCardV2: FixedCardV2LayoutPreview,
  b2bOptimized: B2BOptimizedLayoutPreview,
};

interface CardRendererOptionPreviewProps {
  type: CardRendererPreviewType;
  title: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
}

export const CardRendererOptionPreview: React.FC<CardRendererOptionPreviewProps> = ({
  type,
  title,
  subtitle,
  selected,
  onSelect,
}) => {
  const PreviewComponent = previewMap[type];

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: selected ? 'primary.50' : '#fff',
        cursor: 'pointer',
        p: 0,
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
        boxShadow: selected ? '0 0 0 1px rgba(25, 118, 210, 0.25)' : 'none',
        '&:hover': {
          borderColor: selected ? 'primary.main' : 'primary.light',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: '#f3f4f6',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 108,
        }}
      >
        <PreviewComponent />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: selected ? 'primary.main' : '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {selected && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
            )}
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 3, display: 'block', lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

export default CardRendererOptionPreview;
